import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { GradeClass, GradeSchoolClass, SchoolClass } from '@/models/school-class/school-class';
import { schoolClassService } from '@/service/api/school-class.service';
import FileSaver from 'file-saver';
import { SchoolClassTrendResult, SchoolClassTrendResults } from '@/models/school-class/school-class-trend-results';
import { schoolClassTrendService } from '@/service/api/school-class-trend.service';
import { BulkUpdateSchoolClassesRequest } from '@/models/school-class/school-class-bulk-update';
import { Empty } from '@/models/core/empty';

export interface ISchoolClassState {
  schoolClasses: SchoolClass[]; // クラス一覧
  gradeObjects: GradeClass[]; // 学年一覧
  gradeSchoolClasses: GradeSchoolClass[]; // 学年別クラス一覧
  schoolClassTrendResults: SchoolClassTrendResults; // ある学校の全クラスのトレンドデータ
  schoolClassTrendResult: SchoolClassTrendResult; // 現在選択しているクラスのトレンドデータ
  gradeSchoolClassesIncludeSelected: GradeSchoolClass[]; //学年-クラスマスターに選択クラス情報を追加したもの(編集時のみ使用)
}

@Module({ dynamic: true, store, name: 'schoolClassModule', namespaced: true })
class SchoolClassModule extends VuexModule implements ISchoolClassState {
  schoolClasses: SchoolClass[] = [];
  gradeObjects: GradeClass[] = [];
  gradeSchoolClasses: GradeSchoolClass[] = [];
  schoolClassTrendResults: SchoolClassTrendResults = new SchoolClassTrendResults();
  schoolClassTrendResult: SchoolClassTrendResult = new SchoolClassTrendResult();
  gradeSchoolClassesIncludeSelected: GradeSchoolClass[] = []; //学年-クラスマスターに選択クラス情報を追加したもの(編集時のみ使用)

  get maxGrade(): number {
    return this.schoolClasses.reduce((result: number, schoolClass: SchoolClass) => {
      if (result < schoolClass.grade) result = schoolClass.grade;
      return result;
    }, -1);
  }

  // 学年別クラス一覧を学年順のクラス一覧にする
  get flattenSchoolClasses(): SchoolClass[] {
    return this.gradeSchoolClasses.reduce((result: SchoolClass[], gradeSchoolClass: GradeSchoolClass) => {
      const schoolClasses: SchoolClass[] = gradeSchoolClass.schoolClasses.map((schoolClass: SchoolClass) => {
        schoolClass.grade = gradeSchoolClass.grade;
        const newSchoolClass: SchoolClass = new SchoolClass().deserialize(schoolClass);
        return newSchoolClass;
      });
      result = result.concat(schoolClasses);
      return result;
    }, []);
  }

  get schoolClassesByGrade(): Map<number, SchoolClass[]> {
    return this.schoolClasses.reduce((result: Map<number, SchoolClass[]>, schoolClass: SchoolClass) => {
      if (!result.has(schoolClass.grade)) {
        result.set(schoolClass.grade, []);
      }
      result.get(schoolClass.grade).push(schoolClass);
      return result;
    }, new Map<number, SchoolClass[]>());
  }

  // 学年とクラス名からクラス情報を参照するマップ
  get schoolClassGradeAndClassNameMap(): Map<string, SchoolClass> {
    return this.schoolClasses.reduce((result: Map<string, SchoolClass>, schoolClass: SchoolClass) => {
      result.set(`${schoolClass.grade}-${schoolClass.schoolClassName}`, schoolClass);
      return result;
    }, new Map<string, SchoolClass>());
  }

  get schoolClassIDSet(): Set<number> {
    return this.schoolClasses.reduce((result: Set<number>, schoolClass: SchoolClass) => {
      result.add(schoolClass.schoolClassID);
      return result;
    }, new Set<number>());
  }

  get schoolClassTrendResultBySchoolClassID(): any {
    return (schoolClassID: number) => {
      if (!this.schoolClassTrendResults.data) {
        return;
      }
      return this.schoolClassTrendResults.data.find((trend: SchoolClassTrendResult): boolean => {
        return trend.schoolClassID === schoolClassID;
      });
    };
  }

  get grades(): number[] {
    return this.gradeObjects.map((gradeObject: GradeClass) => gradeObject.grade);
  }

  get schoolClassesIncludeSelected(): SchoolClass[] {
    return this.gradeSchoolClassesIncludeSelected.map((d: GradeSchoolClass) => d.schoolClasses).flat();
  }

  /**
   * クラス用チェックボックスグループ反映(編集時のすでに登録されているクラスのdisabled化に使用)
   * @param selectedSchoolClasses 選択したクラス情報(新規作成時、コピー作成時は空をセットする)
   */
  @Mutation
  setGradeSchoolClassesIncludeSelected(selectedSchoolClasses: SchoolClass[] = []) {
    const selectedSchoolClassSet: Set<number> = selectedSchoolClasses.reduce((result: Set<number>, schoolClass: SchoolClass) => {
      result.add(schoolClass.schoolClassID);
      return result;
    }, new Set<number>());
    this.gradeSchoolClassesIncludeSelected = this.gradeSchoolClasses.map((gradeSchoolClass: GradeSchoolClass) => {
      gradeSchoolClass.schoolClasses = gradeSchoolClass.schoolClasses.map((schoolClass: SchoolClass) => {
        if (selectedSchoolClassSet.has(schoolClass.schoolClassID)) {
          schoolClass.selected = true;
        }
        return schoolClass;
      });
      return gradeSchoolClass;
    });
  }

  @Mutation
  setSchoolClasses(value: SchoolClass[]) {
    this.schoolClasses = value;
  }

  @Mutation
  setGradeObjects(value: GradeClass[]) {
    this.gradeObjects = value;
  }

  @Mutation
  setGradeSchoolClasses(value: GradeSchoolClass[]) {
    this.gradeSchoolClasses = value;
  }

  @Mutation
  setSchoolClassTrendResults(value: SchoolClassTrendResults) {
    this.schoolClassTrendResults = value;
  }
  @Mutation
  setSchoolClassTrendResult(value: SchoolClassTrendResult) {
    this.schoolClassTrendResult = value;
  }
  @Mutation
  insertGradeSchoolClasses(grade: number) {
    const insertIndex: number = this.gradeSchoolClasses.findIndex((gradeSchoolClass: GradeSchoolClass) => gradeSchoolClass.grade > grade);

    const newSchoolClass: SchoolClass = new SchoolClass().deserialize({
      grade: grade,
      schoolClassName: '',
    });
    newSchoolClass.created = true;
    const newGradeSchoolClass: GradeSchoolClass = new GradeSchoolClass().deserialize({
      grade: grade,
      schoolClasses: [newSchoolClass],
    });
    //追加するときに昇順になるようにリストに挿入する
    if (insertIndex < 0) {
      this.gradeSchoolClasses.push(newGradeSchoolClass);
    } else {
      this.gradeSchoolClasses.splice(insertIndex, 0, newGradeSchoolClass);
    }
  }

  @Mutation
  removeGradeSchoolClasses(index: number) {
    this.gradeSchoolClasses.splice(index, 1);
  }

  @Mutation
  cleanSchoolClassTrendResult() {
    this.schoolClassTrendResult = undefined;
  }

  /**
   * クラス一覧取得
   * @param headers 年度変更用のヘッダ
   */
  @Action({ rawError: true })
  async fetchSchoolClasses(headers: any = {}): Promise<SchoolClass[]> {
    const schoolClasses: SchoolClass[] = await schoolClassService.getSchoolClasses(headers);
    this.setSchoolClasses(schoolClasses);

    return schoolClasses;
  }

  /**
   * 学年一覧取得
   */
  @Action({ rawError: true })
  async fetchGrades(headers: any = {}) {
    const grades: GradeClass[] = await schoolClassService.getGradeClass(headers);
    this.setGradeObjects(grades);
  }

  /**
   * 学年込みクラス一覧取得
   * @param headers 年度変更用のヘッダ
   */
  @Action({ rawError: true })
  async fetchGradesSchoolClasses(headers: any = {}) {
    const gradeSchoolClasses: GradeSchoolClass[] = await schoolClassService.getGradeSchoolClasses(headers);
    this.setGradeSchoolClasses(gradeSchoolClasses);
  }

  /**
   * クラス一括登録用テンプレートダウンロード
   */
  @Action({ rawError: true })
  async downloadRegistrationTemplate() {
    const file = await schoolClassService.downloadSchoolClassRegistrationTemplate();
    FileSaver.saveAs(file, 'クラス一括登録用テンプレート.xlsx');
  }

  /**
   * クラス一括登録(Excelインポート)
   * @param formData クラス一括登録用
   */
  @Action({ rawError: true })
  async postSchoolClassFile(formData: FormData) {
    return await schoolClassService.uploadSchoolClassExcelFile(formData);
  }

  /**
   * クラストレンド取得
   */
  @Action({ rawError: true })
  async fetchSchoolClassTrendResults() {
    const schoolClassTrendResults = await schoolClassTrendService.getSchoolClassTrendResults();
    this.setSchoolClassTrendResults(schoolClassTrendResults);
  }

  /**
   * クラス一括更新(追加、更新、削除を一括に行う)
   * @param bulkUpdateSchoolClassesRequest クラス設定更新情報
   */
  @Action({ rawError: true })
  async bulkUpdateSchoolClasses(bulkUpdateSchoolClassesRequest: BulkUpdateSchoolClassesRequest): Promise<Empty> {
    return await schoolClassService.bulkUpdateSchoolClasses(bulkUpdateSchoolClassesRequest);
  }
}

export const schoolClassModule = getModule(SchoolClassModule);
